import type { FC, ReactNode } from 'react'
import type { BandMember, JourneyArt } from '@/types'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { members } from '@/constants/portfolio'
import { audioEngine } from '@/lib/audio/engine'
import InstrumentArt from '@/components/landing/instrument-art'
import SampleButton, { sampleSource } from '@/components/landing/sample-button'

interface MemberDetailProps {
    member: BandMember
    compact?: boolean
    panelId: string
    labelledBy: string
}

const SECONDARY: Partial<Record<string, JourneyArt>> = {
    'atta-rumnan': 'mic',
    'umar-naufal': 'amp'
}

const ART_SIZE: Record<string, string> = {
    'les-paul': 'h-[min(66vh,560px)] w-auto',
    telecaster: 'h-[min(66vh,560px)] w-auto',
    bass: 'h-[min(70vh,600px)] w-auto',
    'drum-kit': 'w-full max-w-[520px] h-auto'
}

const ART_SIZE_COMPACT: Record<string, string> = {
    'les-paul': 'h-[300px] w-auto xs:h-[360px]',
    telecaster: 'h-[300px] w-auto xs:h-[360px]',
    bass: 'h-[320px] w-auto xs:h-[380px]',
    'drum-kit': 'w-full max-w-[420px] h-auto'
}

const MemberArt: FC<{ member: BandMember, compact?: boolean }> = ({ member, compact }): ReactNode => {
    const [shakeKey, setShakeKey] = useState(0)
    const secondary = SECONDARY[member.id]
    const size = (compact ? ART_SIZE_COMPACT : ART_SIZE)[member.instrument]

    return (
        <div className='relative flex items-center justify-center'>
            <div aria-hidden='true' className='halftone absolute inset-[8%] -skew-x-12 opacity-[0.14]' />

            {secondary && (
                <div
                    aria-hidden='true'
                    className={cn(
                        'pointer-events-none absolute bottom-[2%] z-0',
                        secondary === 'mic' ? 'w-[26%] max-w-[150px]' : 'w-[52%] max-w-[260px]',
                        member.layoutAlignment === 'left' ? 'right-[2%]' : 'left-[2%]'
                    )}
                    style={{ transform: `rotate(${member.layoutAlignment === 'left' ? 6 : -6}deg)` }}
                >
                    <InstrumentArt kind={secondary} />
                </div>
            )}

            <button
                type='button'
                aria-label={`${member.sample.label}, ${member.instrumentLabel}`}
                onClick={() => {
                    setShakeKey(key => key + 1)
                    audioEngine.toggle(sampleSource(member))
                }}
                onPointerEnter={() => audioEngine.prepare(sampleSource(member))}
                className='group relative z-10 block cursor-pointer outline-offset-8'
            >
                <span
                    key={shakeKey}
                    className={cn('block transition-transform duration-300 group-hover:-rotate-2 group-hover:scale-[1.02]', shakeKey > 0 && 'anim-shake')}
                >
                    <InstrumentArt kind={member.instrument} className={size} />
                </span>
                <span className='mono-label absolute -bottom-2 left-1/2 hidden -translate-x-1/2 whitespace-nowrap bg-ink px-2 py-1 text-[10px] text-paper opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 tab:block'>
                    Tap the {member.instrumentLabel.toLowerCase()}
                </span>
            </button>
        </div>
    )
}

// One member on the select screen: name, the instrument cutout, the bio plate and the sample.
const MemberDetail: FC<MemberDetailProps> = ({ member, compact, panelId, labelledBy }): ReactNode => {
    const flip = member.layoutAlignment === 'right'

    return (
        <div
            id={panelId}
            role='tabpanel'
            aria-labelledby={labelledBy}
            tabIndex={0}
            className={cn('relative outline-offset-4', compact ? 'flex flex-col gap-6' : cn('grid h-full items-center gap-8 wide:gap-12', flip ? 'grid-cols-[3fr_2fr]' : 'grid-cols-[2fr_3fr]'))}
        >
            <div key={`art-${member.id}`} className={cn('anim-wipe-in', !compact && flip && 'order-2')}>
                <MemberArt member={member} compact={compact} />
            </div>

            <div key={`text-${member.id}`} className={cn('anim-rise-in relative', !compact && flip && 'order-1')}>
                <p className='mono-label text-[11px] text-ink-soft'>
                    Player {String(member.order).padStart(2, '0')} / {String(members.length).padStart(2, '0')}
                </p>

                <h3 className={cn('display-type mt-2', compact ? 'text-[clamp(3.5rem,18vw,6rem)]' : 'text-[clamp(3.2rem,min(7.6vw,13vh),8.5rem)]')}>
                    {member.shortName}
                </h3>

                <p className='mono-label mt-3 text-[11px]'>
                    {member.stageName} <span className='px-1.5 text-ink-soft'>/</span> {member.age} years old
                </p>

                <div className={cn('plate mt-5', compact ? 'p-5' : 'p-[clamp(1rem,2.6vh,1.5rem)]')}>
                    <p className='display-type menu-skew origin-left text-[clamp(1.4rem,2vw,1.9rem)]'>{member.primaryRole}</p>

                    <p className='mt-3 flex flex-wrap items-center gap-x-2 gap-y-1.5'>
                        <span className='mono-label text-[10px] text-ink-soft'>Influences</span>
                        {member.influences.map(influence => (
                            <span key={influence} className='border border-ink px-2 py-0.5 text-[13px] font-semibold'>{influence}</span>
                        ))}
                    </p>

                    {member.secondarySkills.length > 0 && (
                        <p className='mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5'>
                            <span className='mono-label text-[10px] text-ink-soft'>Also</span>
                            {member.secondarySkills.map(skill => (
                                <span key={skill} className='bg-ink px-2 py-0.5 text-[13px] font-semibold text-paper'>{skill}</span>
                            ))}
                        </p>
                    )}

                    <p className={cn('mt-4 max-w-[62ch] leading-[1.6]', compact ? 'text-[16px]' : 'text-[clamp(15px,2.2vh,17.5px)]')}>{member.biography}</p>

                    <SampleButton member={member} className='mt-5' />
                </div>
            </div>
        </div>
    )
}

export default MemberDetail
